import type { ApiChat } from '../../types/chats';

import { debounce, throttle } from '../../../util/schedulers';
import { fetchOpenImConversations } from '../methods/conversations';
import { noteFullReplaceFallback } from './uxPerfCounters';

const DEBOUNCE_MS = 280;
const THROTTLE_MS = 5000;

let inFlight = false;
let queued = false;
let currentSelfUserId: string | undefined;
const subscribers = new Set<(chats: ApiChat[]) => void>();

// Set once per login by `startOpenImRealtimeBridge` -- `fetchOpenImConversations` needs it to
// resolve each conversation's `latestMsg` (whether it's outgoing/incoming), and this module has no
// other access to the current session's user id (it isn't wired into global state).
export function setConversationSyncSelfUserId(selfUserId: string): void {
  currentSelfUserId = selfUserId;
}

async function runFetch() {
  if (inFlight) {
    queued = true;
    return;
  }
  if (!currentSelfUserId) {
    return;
  }

  inFlight = true;
  try {
    const chats = await fetchOpenImConversations(currentSelfUserId);
    noteFullReplaceFallback('conversation_full_refresh');
    subscribers.forEach((cb) => cb(chats));
  } finally {
    inFlight = false;
    if (queued) {
      queued = false;
      debouncedForceRefresh();
    }
  }
}

const throttledRefresh = throttle(runFetch, THROTTLE_MS, false);
const debouncedForceRefresh = debounce(runFetch, DEBOUNCE_MS, false, true);

export function requestConversationRefresh(force = false): void {
  if (force) {
    // A pure trailing debounce can be starved indefinitely: a burst of `OnRecvNewMessage`/
    // `OnConversationChanged` events arriving faster than `DEBOUNCE_MS` apart keeps resetting
    // `debouncedForceRefresh`'s timer, so `runFetch` (and the read-state baseline it establishes
    // via `mergeRefreshedOpenImConversations`) may never fire for the whole burst. `throttledRefresh`
    // uses `setInterval`, which repeat calls cannot reset -- pairing it in guarantees at least one
    // refresh every `THROTTLE_MS` regardless of how long the burst continues.
    debouncedForceRefresh();
    throttledRefresh();
  } else {
    throttledRefresh();
  }
}

export function onConversationRefreshed(cb: (chats: ApiChat[]) => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}
